/**
 * DevicesIndicators - Indicadores de ignição, bloqueio, velocidade, alarme
 * Versão Premium com tokens semânticos - DARK/LIGHT MODE
 */

import React from 'react';
import {
 Box, Stack, Tooltip, Typography 
} from '@mui/material';
import PowerIcon from '@mui/icons-material/Power';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';

const DevicesIndicators = ({ ignition, blocked, speed, alarm, compact = false }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const speedKmh = Math.round((speed || 0) * 1.852);
  const isDark = tokens?.isDark ?? true;

  // Sizes based on compact mode
  const size = compact ? 20 : 26;
  const iconSize = compact ? 12 : 15;
  const speedWidth = compact ? 36 : 46;
  const speedFontSize = compact ? 9 : 11;

  // Cores DIFERENTES para dark/light mode
  const colors = isDark ? {
    // DARK MODE
    success: '#00d4aa',
    successBg: 'rgba(0, 212, 170, 0.15)',
    error: '#f85149',
    errorBg: 'rgba(248, 81, 73, 0.15)',
    warning: '#d29922',
    warningBg: 'rgba(210, 153, 34, 0.15)',
    info: '#58a6ff',
    infoBg: 'rgba(88, 166, 255, 0.15)',
    muted: '#6e7681',
    mutedBg: 'rgba(110, 118, 129, 0.15)',
  } : {
    // LIGHT MODE - cores mais escuras para contraste
    success: '#00875a',
    successBg: 'rgba(0, 135, 90, 0.15)',
    error: '#cf222e',
    errorBg: 'rgba(207, 34, 46, 0.15)',
    warning: '#9a6700',
    warningBg: 'rgba(154, 103, 0, 0.15)',
    info: '#0969da',
    infoBg: 'rgba(9, 105, 218, 0.15)',
    muted: '#656d76',
    mutedBg: 'rgba(101, 109, 118, 0.12)',
  };

  return (
    <Stack direction="row" spacing={compact ? 0.25 : 0.5} alignItems="center">
      {/* Ignition */}
      <Tooltip title={ignition === true ? t('positionIgnitionOn') : ignition === false ? t('positionIgnitionOff') : t('positionIgnition')}>
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: ignition === true ? colors.successBg :
                     ignition === false ? colors.mutedBg : colors.mutedBg,
          }}
        >
          {ignition === true ? (
            <PowerIcon sx={{ fontSize: iconSize, color: colors.success }} />
          ) : (
            <PowerOffIcon sx={{ fontSize: iconSize, color: colors.muted }} />
          )}
        </Box>
      </Tooltip>

      {/* Blocked */}
      <Tooltip title={blocked === true ? t('positionBlocked') : blocked === false ? t('positionUnblocked') : t('positionBlocked')}>
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: blocked === true ? colors.errorBg :
                     blocked === false ? colors.successBg : colors.mutedBg,
          }}
        >
          {blocked === true ? (
            <LockIcon sx={{ fontSize: iconSize, color: colors.error }} />
          ) : (
            <LockOpenIcon sx={{ fontSize: iconSize, color: blocked === false ? colors.success : colors.muted }} />
          )}
        </Box>
      </Tooltip>

      {/* Speed */}
      <Tooltip title={`${t('positionSpeed')}: ${speedKmh} km/h`}>
        <Box
          sx={{
            minWidth: speedWidth,
            height: size,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.3,
            bgcolor: speedKmh > 0 ? colors.infoBg : colors.mutedBg,
            px: 0.5,
          }}
        >
          <SpeedIcon sx={{ fontSize: iconSize - 2, color: speedKmh > 0 ? colors.info : colors.muted }} />
          <Typography variant="caption" sx={{ fontSize: speedFontSize, fontWeight: 600, color: speedKmh > 0 ? colors.info : colors.muted }}>
            {speedKmh}
          </Typography>
        </Box>
      </Tooltip>

      {/* Alarm (only if active) */}
      {alarm && (
        <Tooltip title={`${t('eventAlarm')}: ${alarm}`}>
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: colors.warningBg,
              animation: 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          >
            <WarningAmberIcon sx={{ fontSize: iconSize, color: colors.warning }} />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

export default React.memo(DevicesIndicators);
